import styled from 'styled-components'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { FormControl, InputLabel, TextField } from '@mui/material'
import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete'
import { useEffect, useMemo, useRef } from 'react'
import { rem } from 'theme/materialUITheme'
import { RemoveIcon } from 'assets/icons/remove-icon'

interface Props {
  name: string
  label?: JSX.Element | string
  form: UseFormReturn<any>
  options: SelectOption[]
  initialValue?: SelectOption[]
  status: RequestStatus
  onRemove?: (arg0: string | number) => void
  confirmRemoveMessage?: string
  disabled?: boolean
  singleValue?: boolean
  clearOptions?: () => void
  search?: (searchString: string) => void
  disableRemove?: boolean
  disableAdd?: boolean
  lockedKeys?: (string | number)[]
}

export default function SearchableClubSelect({
  name,
  label,
  form,
  options,
  status,
  onRemove,
  confirmRemoveMessage,
  disabled = false,
  search,
  disableRemove = false,
  disableAdd = false,
  initialValue,
  lockedKeys,
}: Props): JSX.Element {
  const { control, setValue, watch, getValues } = form
  const values = watch(name, [])

  const intl = useIntl()
  const initialValueRef = useRef<SelectOption[]>([])

  // `initialValue` is built from the clubs that are currently loaded, which is only ever the
  // latest search result. Replacing the selection with it would drop every club whose data is
  // not part of that result, so the resolved labels are merged into what is already selected.
  useEffect(() => {
    if (status !== 'succeeded' || !initialValue || initialValueRef.current.length > 0) {
      return
    }

    initialValueRef.current = initialValue

    const current: SelectOption[] = getValues(name) ?? []
    const resolved = current.map(
      (value) => initialValue.find((option) => String(option.key) === String(value.key)) ?? value,
    )
    const missing = initialValue.filter((option) => !current.some((value) => String(value.key) === String(option.key)))
    const next = [...resolved, ...missing]

    const isUnchanged =
      next.length === current.length &&
      next.every((option, index) => option.key === current[index].key && option.label === current[index].label)

    if (!isUnchanged) {
      setValue(name, next)
    }
  }, [status, initialValue, name, setValue, getValues])

  // Saved club ids are strings while the options are built from numeric club ids,
  // so every comparison has to go through String().
  const lockedKeySet = useMemo(() => new Set((lockedKeys ?? []).map(String)), [lockedKeys])

  const canRemove = (option: SelectOption) => !disabled && (!disableRemove || !lockedKeySet.has(String(option.key)))

  // TODO: check if this is needed, doesn't render with this
  // const handleChange = useCallback(
  //   (newValues: SelectOption[]) => {
  //     setValue(name, newValues)
  //   },
  //   [name, setValue],
  // )

  const handleChange = (newValues: SelectOption[]) => {
    return newValues
  }

  const handleRemove = (option: SelectOption) => {
    if (confirmRemoveMessage && window.confirm(confirmRemoveMessage)) {
      doRemove(option)
    } else if (!confirmRemoveMessage) {
      doRemove(option)
    }
  }

  const doRemove = (option: SelectOption) => {
    const newValues = values.filter((value: SelectOption) => String(value.key) !== String(option.key))
    setValue(name, newValues)
    if (onRemove) {
      onRemove(option.key)
    }
  }

  const searchRequest = (searchString: string | undefined) => {
    if (search && searchString) {
      search(searchString)
    }
  }

  const getNoOptionsText = () => {
    return search
      ? intl.formatMessage({ id: 'strings.typeMin3Characters' })
      : intl.formatMessage({ id: 'strings.noOptions' })
  }

  return (
    <>
      <InputContainer margin="normal" fullWidth disabled={disabled}>
        {label && <FieldLabel>{label}</FieldLabel>}
        <Controller
          control={control}
          name={name}
          defaultValue={values}
          render={({ field: { onChange } }) => (
            <Autocomplete
              multiple
              loading={status === 'loading'}
              disableListWrap
              onChange={(event, newValues, reason: AutocompleteChangeReason) => {
                if (disableAdd && reason === 'selectOption') {
                  return
                }
                if (disableRemove && reason === 'removeOption') {
                  return
                }
                if (reason !== 'removeOption') {
                  const nextValues = handleChange(newValues)
                  onChange(nextValues)
                }
              }}
              onInputChange={(event: any) => searchRequest(event?.target?.value)}
              options={options}
              getOptionLabel={(option) => option.label}
              filterSelectedOptions
              autoHighlight
              value={values}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={intl.formatMessage({ id: 'strings.search' })}
                  sx={{
                    '& .MuiAutocomplete-inputRoot': {
                      display: 'flex',
                      padding: 'var(--p-25, 10px) var(--p-3, 12px)',
                      alignItems: 'center',
                      gap: '8px',
                      alignSelf: 'stretch',
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',

                      '& fieldset': {
                        borderColor: '#e4e4e7',
                      },

                      '&:hover fieldset': {
                        borderColor: '#e4e4e7',
                      },

                      '&.Mui-focused fieldset': {
                        borderColor: '#e4e4e7',
                      },

                      '& .MuiAutocomplete-input': {
                        padding: 0,
                      },
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      color: '#8A8A8E',
                    },
                  }}
                />
              )}
              disabled={disabled || disableAdd}
              noOptionsText={getNoOptionsText()}
              isOptionEqualToValue={(option, value) => String(option.key) === String(value.key)}
            />
          )}
        />
      </InputContainer>
      <ValueContainer>
        {values.map((option) => {
          return (
            <div
              key={option.key}
              data-index={option.key}
              style={{ display: 'flex', fontSize: rem(16), alignItems: 'center', gap: rem(10), cursor: 'pointer' }}
            >
              {canRemove(option) && (
                <RemoveIcon
                  onClick={() => {
                    handleRemove(option)
                  }}
                />
              )}
              <span style={{ color: '#373D40' }}>{option.label}</span>
            </div>
          )
        })}
      </ValueContainer>
    </>
  )
}

const InputContainer = styled(FormControl)`
  margin: 20px 0;

  & .MuiChip-root,
  .MuiAutocomplete-clearIndicator,
  .MuiAutocomplete-popupIndicator {
    display: none;
  }

  & .MuiAutocomplete-inputRoot {
    padding: 10px !important;
  }
`

const FieldLabel = styled(InputLabel)`
  margin-top: -20px;
  transition: none;
  transform: none;
`

const ValueContainer = styled.div`
  padding: 20px;
  background-color: ${(props) => props.theme.customPalette.lightGray};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.mediumGray};
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 1.25em;
  min-height: 242px;
  border-radius: 5px;

  & > div {
    margin-bottom: 10px;

    & > span.delete {
      margin-left: 20px;
      cursor: pointer;
      color: ${(props) => props.theme.customPalette.error};
    }
  }
`
